<template>
  <div class="comment-thread">
    <v-card flat outlined class="mb-4">
      <v-card-text>
        <v-textarea
          v-model="newCommentBody"
          :label="$t('COMMENTS.ADD')"
          rows="3"
          auto-grow
          :disabled="!canEdit"
        />
        <v-btn
          color="primary"
          small
          :disabled="!canEdit || !newCommentBody.trim()"
          :loading="submitting"
          @click="submitComment"
        >
          {{ $t("COMMENTS.POST") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="!comments.length" class="grey--text text--darken-1 py-2">
      {{ $t("COMMENTS.EMPTY") }}
    </div>

    <div v-else>
      <div v-for="comment in comments" :key="comment.id" class="comment-thread__item mb-4">
        <div class="d-flex justify-space-between align-start">
          <div>
            <div class="font-weight-medium">{{ comment.user && comment.user.name }}</div>
            <div class="text-caption grey--text">{{ formatDate(comment.created_at) }}</div>
          </div>
        </div>
        <div class="mt-2 comment-thread__body">{{ comment.body }}</div>

        <div v-if="comment.replies && comment.replies.length" class="comment-thread__replies ml-6 mt-3">
          <div v-for="reply in comment.replies" :key="reply.id" class="mb-3">
            <div class="font-weight-medium">{{ reply.user && reply.user.name }}</div>
            <div class="text-caption grey--text">{{ formatDate(reply.created_at) }}</div>
            <div class="mt-1">{{ reply.body }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_DEAL_COMMENTS, CREATE_DEAL_COMMENT } from "@/store/deals.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "CommentThread",
  props: {
    dealId: {
      type: [Number, String],
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      comments: [],
      newCommentBody: "",
      loading: false,
      submitting: false,
    };
  },
  watch: {
    dealId: {
      immediate: true,
      handler() {
        this.loadComments();
      },
    },
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "";
      }
      return new Date(value).toLocaleString();
    },
    async loadComments() {
      if (!this.dealId) {
        this.comments = [];
        return;
      }
      this.loading = true;
      try {
        this.comments = await this.$store.dispatch(GET_DEAL_COMMENTS, this.dealId);
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    async submitComment() {
      if (!this.newCommentBody.trim()) {
        return;
      }
      this.submitting = true;
      try {
        await this.$store.dispatch(CREATE_DEAL_COMMENT, {
          dealId: this.dealId,
          body: this.newCommentBody.trim(),
        });
        this.newCommentBody = "";
        await this.loadComments();
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.comment-thread__body {
  white-space: pre-wrap;
}
</style>
